import React from "react";
import {
  ChevronLeft,
  Edit3,
  Trash2,
  Ban,
  Shield,
  AlertTriangle,
  User,
  Clock,
  RefreshCw,
  Megaphone,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { config } from "../../config/environment";

interface UserProfileHeaderProps {
  user: any;
  onEditUser: () => void;
  onDeleteUser: () => void;
  onSuspendUser: () => void;
  isAdmin: boolean;
  onRefresh?: () => void;
  refreshing?: boolean;
  onToggleMarketing?: (next: boolean) => void;
  marketingTogglePending?: boolean;
}

const UserProfileHeader: React.FC<UserProfileHeaderProps> = ({
  user,
  onEditUser,
  onDeleteUser,
  onSuspendUser,
  isAdmin,
  onRefresh,
  refreshing,
  onToggleMarketing,
  marketingTogglePending,
}) => {
  const navigate = useNavigate();
  const getStatusColor = (status: string, isSuspended: boolean) => {
    if (isSuspended) return "bg-red-100 text-red-800";
    switch (status) {
      case "ACTIVE":
        return "bg-green-100 text-green-800";
      case "INACTIVE":
        return "bg-yellow-100 text-yellow-800";
      case "PENDING":
        return "bg-blue-100 text-blue-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getStatusIcon = (status: string, isSuspended: boolean) => {
    if (isSuspended) return <Ban className="h-4 w-4 text-red-600" />;
    switch (status) {
      case "ACTIVE":
        return <Shield className="h-4 w-4 text-green-600" />;
      case "INACTIVE":
        return <AlertTriangle className="h-4 w-4 text-yellow-600" />;
      case "PENDING":
        return <Clock className="h-4 w-4 text-blue-600" />;
      default:
        return <Shield className="h-4 w-4 text-gray-600" />;
    }
  };

  const getImageUrl = (url?: string) => {
    if (!url) return undefined;
    if (url.startsWith("http://") || url.startsWith("https://")) return url;
    return `${config.apiUrl}${url.startsWith("/") ? url : `/${url}`}`;
  };

  const getInitials = (first?: string, last?: string, fallback?: string) => {
    const a = (first || "").trim();
    const b = (last || "").trim();
    if (a || b) return `${a.charAt(0)}${b.charAt(0)}`.toUpperCase();
    if (fallback) return fallback.slice(0, 2).toUpperCase();
    return "U"; // Unknown
  };

  // Parse avatar scheme like: avatar:#03A9F4:C  (color:initials)
  const parseAvatarScheme = (value?: string) => {
    if (!value || !value.startsWith("avatar:")) return null;
    try {
      const payload = value.replace("avatar:", "");
      const [color, initials] = payload.split(":");
      return {
        color: color || "#CBD5E1",
        initials: (initials || "U").substring(0, 2).toUpperCase(),
      };
    } catch {
      return null;
    }
  };

  return (
    <div className="bg-white shadow rounded-lg p-6 mb-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center text-gray-600 hover:text-gray-900 transition-colors"
          >
            <ChevronLeft className="h-4 w-4 mr-2" />
            Back to Users
          </button>
          {onRefresh && (
            <button
              onClick={onRefresh}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
              title="Refresh"
            >
              <RefreshCw
                className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`}
              />
              <span className="text-sm">Refresh</span>
            </button>
          )}
        </div>

        {isAdmin && (
          <div className="flex items-center gap-3">
            {onToggleMarketing && (
              <label
                className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-colors cursor-pointer select-none ${
                  user?.featureFlags?.marketing
                    ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                    : "border-gray-200 bg-gray-50 text-gray-600"
                } ${marketingTogglePending ? "opacity-60 cursor-wait" : ""}`}
                title="Toggle access to Marketing Agent & Quick Post"
              >
                <Megaphone className="h-4 w-4" />
                <span className="text-sm font-medium">Marketing</span>
                <span
                  className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                    user?.featureFlags?.marketing
                      ? "bg-emerald-500"
                      : "bg-gray-300"
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                      user?.featureFlags?.marketing
                        ? "translate-x-4"
                        : "translate-x-0.5"
                    }`}
                  />
                </span>
                <input
                  type="checkbox"
                  className="sr-only"
                  checked={!!user?.featureFlags?.marketing}
                  disabled={marketingTogglePending}
                  onChange={(e) => onToggleMarketing(e.target.checked)}
                />
              </label>
            )}

            <button
              onClick={onEditUser}
              className="flex items-center gap-2 px-4 py-2 text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
            >
              <Edit3 className="h-4 w-4" />
              Edit User
            </button>

            <button
              onClick={onSuspendUser}
              className="flex items-center gap-2 px-4 py-2 text-orange-600 bg-orange-50 rounded-lg hover:bg-orange-100 transition-colors"
            >
              <Ban className="h-4 w-4" />
              {user?.isSuspended ? "Unsuspend" : "Suspend"}
            </button>

            <button
              onClick={onDeleteUser}
              className="flex items-center gap-2 px-4 py-2 text-red-600 bg-red-50 rounded-lg hover:bg-red-100 transition-colors"
            >
              <Trash2 className="h-4 w-4" />
              Delete User
            </button>
          </div>
        )}
      </div>

      <div className="flex flex-col lg:flex-row lg:items-center gap-6">
        {/* Profile Picture */}
        <div className="flex-shrink-0">
          <div
            className="h-24 w-24 rounded-full flex items-center justify-center overflow-hidden"
            style={{
              backgroundColor:
                parseAvatarScheme(user?.profilePicture)?.color || "#E5E7EB",
            }}
          >
            {(() => {
              const avatar = parseAvatarScheme(user?.profilePicture);
              const imgUrl = getImageUrl(user?.profilePicture);
              if (avatar) {
                return (
                  <span className="text-white text-2xl font-semibold">
                    {avatar.initials ||
                      getInitials(
                        user?.firstName,
                        user?.lastName,
                        user?.fullName
                      )}
                  </span>
                );
              }
              if (imgUrl && !user?.profilePicture?.startsWith("avatar:")) {
                return (
                  <img
                    src={imgUrl}
                    alt={user.fullName}
                    className="h-24 w-24 object-cover"
                  />
                );
              }
              return <User className="h-12 w-12 text-gray-400" />;
            })()}
          </div>
        </div>

        {/* User Info */}
        <div className="flex-1">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {user?.fullName || "Unknown User"}
              </h1>
              <p className="text-gray-600">{user?.email}</p>
              {user?.username && (
                <p className="text-sm text-gray-500">@{user.username}</p>
              )}
            </div>

            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                {getStatusIcon(user?.status, user?.isSuspended)}
                <span
                  className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(
                    user?.status,
                    user?.isSuspended
                  )}`}
                >
                  {user?.isSuspended ? "Suspended" : user?.status || "Unknown"}
                </span>
              </div>

              {user?.isVerified && (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  <Shield className="h-3 w-3 mr-1" />
                  Verified
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserProfileHeader;
